import React, { useState } from 'react';
import { Tree, Button, Space, Typography, Tag, Modal, Input, message, Popconfirm } from 'antd';
import {
  FolderOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  FileTextOutlined,
  DeleteOutlined
} from '@ant-design/icons';

const { Text } = Typography;

function FolderTree({ 
  folders, 
  documents,
  selectedFolderId, 
  onSelectFolder,
  onCreateFolder,
  onDeleteFolder,
  loading 
}) {
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [parentFolderIdForCreate, setParentFolderIdForCreate] = useState(null);
  const [expandedKeys, setExpandedKeys] = useState(['all']);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState(null);

  const unfiledDocuments = documents.filter(d => !d.folder_id);
  const totalDocuments = documents.length;

  const buildTreeData = () => {
    const treeData = [
      {
        key: 'all',
        title: (
          <Space>
            <FileTextOutlined style={{ color: '#0B3D91' }} />
            <Text strong>All Documents</Text>
            <Tag size="small">{totalDocuments}</Tag>
          </Space>
        ),
        isLeaf: true,
        selectable: true
      }
    ];

    const buildFolderNodes = (parentId = null) => {
      return folders
        .filter(f => f.parent_folder_id === parentId)
        .map(folder => {
          const folderDocs = documents.filter(d => d.folder_id === folder.id);
          const children = buildFolderNodes(folder.id);
          
          return {
            key: `folder-${folder.id}`,
            title: (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                <Space>
                  <Text>{folder.name}</Text>
                  <Tag size="small" color="blue">{folderDocs.length}</Tag>
                </Space>
                <Space size={8} style={{ marginLeft: 'auto' }}>
                  <Button 
                    type="default"
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      console.log('➕ Add subfolder clicked for folder:', folder.id);
                      setParentFolderIdForCreate(folder.id);
                      setCreateModalVisible(true);
                    }}
                    style={{ 
                      color: '#0B3D91', 
                      borderColor: '#0B3D91',
                      background: '#f0f5ff',
                      minWidth: '28px',
                      height: '24px'
                    }}
                    title="Add Subfolder"
                  />
                  <Popconfirm
                    title="Delete Folder?"
                    description={`This will delete "${folder.name}" and move all documents to unfiled.`}
                    onConfirm={(e) => {
                      e?.stopPropagation?.();
                      console.log('🗑️ [FolderTree] Delete confirmed for folder:', folder.id, folder.name);
                      onDeleteFolder(folder.id);
                    }}
                    okText="Yes, Delete"
                    cancelText="Cancel"
                    okButtonProps={{ danger: true }}
                    placement="right"
                  >
                    <Button 
                      type="default"
                      size="small" 
                      icon={<DeleteOutlined />}
                      danger
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        console.log('🗑️ [FolderTree] Delete button clicked for folder:', folder.id);
                      }}
                      style={{ 
                        background: '#fff1f0',
                        minWidth: '28px',
                        height: '24px'
                      }}
                      title="Delete Folder"
                    />
                  </Popconfirm>
                </Space>
              </div>
            ),
            icon: ({ expanded }) => expanded ? <FolderOpenOutlined style={{ color: '#0B3D91' }} /> : <FolderOutlined style={{ color: '#0B3D91' }} />,
            children: children.length > 0 ? children : undefined,
            isLeaf: children.length === 0
          };
        });
    };

    const folderNodes = buildFolderNodes();
    if (folderNodes.length > 0) {
      treeData.push(...folderNodes);
    }

    if (unfiledDocuments.length > 0 || folderNodes.length > 0) {
      treeData.push({
        key: 'unfiled',
        title: (
          <Space>
            <FileTextOutlined style={{ color: '#6B7280' }} />
            <Text type="secondary">Unfiled Documents</Text>
            <Tag size="small">{unfiledDocuments.length}</Tag>
          </Space>
        ),
        isLeaf: true,
        selectable: true
      });
    }

    return treeData;
  };

  const handleSelect = (selectedKeys) => {
    if (selectedKeys.length === 0) return;
    
    const key = selectedKeys[0];
    if (key === 'all') {
      onSelectFolder(null, 'all');
    } else if (key === 'unfiled') {
      onSelectFolder(null, 'unfiled');
    } else if (key.startsWith('folder-')) {
      const folderId = parseInt(key.replace('folder-', ''));
      onSelectFolder(folderId, 'folder');
    }
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) {
      message.error('Please enter a folder name');
      return;
    }
    
    try {
      await onCreateFolder(newFolderName.trim(), parentFolderIdForCreate);
      setCreateModalVisible(false);
      setNewFolderName('');
      setParentFolderIdForCreate(null);
    } catch (error) {
      // Error message is already handled by onCreateFolder (in Documents.js)
      console.error('Folder creation error:', error);
    }
  };

  const getSelectedKey = () => {
    if (selectedFolderId === null) return ['all'];
    if (selectedFolderId === 'unfiled') return ['unfiled'];
    return [`folder-${selectedFolderId}`];
  };

  return (
    <div>
      <div style={{ marginBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Text strong style={{ fontSize: '14px' }}>Folders</Text>
        <Button 
          type="text" 
          size="small" 
          icon={<PlusOutlined />}
          onClick={() => {
            setParentFolderIdForCreate(null);
            setCreateModalVisible(true);
          }}
        >
          New
        </Button>
      </div>
      
      <Tree
        showIcon
        blockNode
        selectedKeys={getSelectedKey()}
        expandedKeys={expandedKeys}
        onExpand={setExpandedKeys}
        onSelect={handleSelect}
        treeData={buildTreeData()}
        style={{ 
          backgroundColor: '#fafafa', 
          borderRadius: '6px', 
          padding: '8px',
          minHeight: '200px'
        }}
      />

      <Modal
        title={parentFolderIdForCreate ? 'Create Subfolder' : 'Create New Folder'}
        open={createModalVisible}
        onOk={handleCreateFolder}
        onCancel={() => {
          setCreateModalVisible(false);
          setNewFolderName('');
          setParentFolderIdForCreate(null);
        }}
        okText="Create"
      >
        <Input
          placeholder="Enter folder name"
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
          onPressEnter={handleCreateFolder}
          autoFocus
        />
        {parentFolderIdForCreate && (
          <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '13px' }}>
            Creating subfolder inside: {folders.find(f => f.id === parentFolderIdForCreate)?.name}
          </Text>
        )}
      </Modal>

      <Modal
        title="Delete Folder?"
        open={deleteModalVisible}
        onOk={async () => {
          if (folderToDelete) {
            console.log('🗑️ [FolderTree] Delete confirmed, calling onDeleteFolder:', folderToDelete.id);
            await onDeleteFolder(folderToDelete.id);
            setDeleteModalVisible(false);
            setFolderToDelete(null);
          }
        }}
        onCancel={() => {
          setDeleteModalVisible(false);
          setFolderToDelete(null);
        }}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        {folderToDelete && (
          <Text>
            This will delete "{folderToDelete.name}" and move all documents to unfiled. Continue?
          </Text>
        )}
      </Modal>
    </div>
  );
}

export default FolderTree;
